import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, RefreshCw, Search } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Sankey,
  Tooltip,
  XAxis,
  YAxis,
  type SankeyLinkProps,
  type SankeyNodeProps,
} from 'recharts';
import {
  api,
  staleNotice,
  type CellularActivity,
  type CellularDay,
  type CellularOrg,
  type CellularRouter,
  type CellularSim,
  type CellularSims,
  type CellularWanCheck,
} from '../lib/api';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Drawer } from '../components/ui/Drawer';
import { Fact, FactSection } from '../components/ui/Facts';
import { InfoTip } from '../components/ui/InfoTip';
import { LoadingTicker } from '../components/LoadingTicker';
import { RefreshProgress } from '../components/RefreshProgress';
import { useSnapshotRefresh } from '../lib/use-snapshot-refresh';
import { SortableHeader } from '../components/ui/SortableHeader';
import { useSort } from '../lib/sort';
import { formatDateTime, formatNumber, formatRelative, formatUsd } from '../lib/format';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/use-auth';

const ACTIVITY_LABEL: Record<CellularActivity, string> = {
  'on-cellular': 'On cellular',
  'using-data': 'Using data',
  standby: 'Standby',
  inactive: 'Inactive',
};

const ACTIVITY_HELP: Record<CellularActivity, string> = {
  'on-cellular': 'RMS reports the router on 4G (checked in within 2 hours).',
  'using-data': 'Over 20 MB/day of 4G data in Hologram, unconfirmed by RMS (stale, or reports wired).',
  standby: 'Keepalive traffic only (under 20 MB/day).',
  inactive: 'SIM paused or being deactivated.',
};

// What a refresh does, in order. The reads run in parallel on the server, so
// this is the shape of the work rather than live progress.
const LOAD_STEPS = [
  'Checking the shared snapshot…',
  'Reading Hologram SIMs, billing and daily usage…',
  "Topping up Hologram's charges ledger…",
  'Asking Teltonika RMS which sites are on 4G…',
  'Working out how long each has been on 4G…',
  'Looking up X5 servers and customers…',
];
const SLOW_MESSAGE =
  'Hologram is slow to answer — still waiting. If it does not reply, the last good copy is shown instead.';

const WINDOWS = [1, 3, 7, 30, 90] as const;
type WindowDays = (typeof WINDOWS)[number];

/** A sparkline needs some width to have a shape, so short windows still draw a week. */
const MIN_TREND_DAYS = 7;

/** Plans at or above this per-MB rate cost ~10× the cheapest; worth seeing at a glance. */
const EXPENSIVE_PER_MB = 0.2;
/** The cheapest plan in use, for the "what if it were on G3" estimate. */
const G3_PER_MB = 0.03;

type View = 'sims' | 'customers' | 'routers';
type Filter = 'attention' | 'all' | CellularActivity;
type ChartMode = 'top' | 'fleet';
type Panel = { kind: 'customer'; key: string } | { kind: 'sim'; deviceId: number; from: string | null } | null;

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'attention', label: 'Needs attention' },
  { value: 'all', label: 'All' },
  { value: 'on-cellular', label: 'On cellular' },
  { value: 'using-data', label: 'Using data' },
  { value: 'standby', label: 'Standby' },
  { value: 'inactive', label: 'Inactive' },
];

interface ChartSeries {
  key: string;
  label: string;
  color: string;
}

const axisTick = { fontSize: 11, fill: 'var(--color-muted-foreground)' };

interface SimRow {
  sim: CellularSim;
  /** Bytes per day over the selected window. */
  window: number[];
  total: number;
  /** What Hologram charged over the window; null until charges are loaded. */
  charged: number | null;
  /** Bytes × plan rate — used only where no actual figure exists. */
  estimate: number | null;
  /** How long the SIM has been in its current state; see `streakInfo`. */
  streak: StreakInfo;
}

interface CustomerRow {
  key: string;
  label: string;
  fromTag: boolean;
  organization: CellularOrg | null;
  sites: SimRow[];
  onCellular: number;
  usingData: number;
  window: number[];
  total: number;
  charged: number | null;
}

/** RMS and Hologram report UTC wall-clock times with no zone suffix. */
function utcIso(value: string | null | undefined): string | null {
  return value ? `${value.replace(' ', 'T')}Z` : null;
}

function sum(values: number[]): number {
  return values.reduce((s, v) => s + v, 0);
}

function windowLabel(days: number): string {
  return days === 1 ? 'day' : `${days} days`;
}

/**
 * Decimal units throughout this page (1 MB = 10^6 bytes), because that is how
 * Hologram bills: the shared `formatBytes` is binary, which made a row read
 * 1.75 GB beside a cost computed on 1.88 GB.
 */
function formatData(bytes: number | null | undefined): string {
  if (bytes == null) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit++;
  }
  const digits = unit === 0 || value >= 100 ? 0 : value >= 10 ? 1 : 2;
  return `${value.toFixed(digits)} ${units[unit]}`;
}

/** Axis ticks land on round decimal values, so they read cleanly without decimals. */
function axisData(bytes: number): string {
  if (bytes === 0) return '0';
  if (bytes >= 1e9) return `${+(bytes / 1e9).toFixed(1)} GB`;
  if (bytes >= 1e6) return `${+(bytes / 1e6).toFixed(0)} MB`;
  return `${+(bytes / 1e3).toFixed(0)} KB`;
}

/**
 * A figure plus its daily average over the window, the average on a muted
 * second line. Zero days count — the average is what the window cost per day,
 * not per active day — and it is omitted on a 1-day window, where it would just
 * repeat the total.
 */
function WithAverage({ children, perDay }: { children: ReactNode; perDay: ReactNode | null }) {
  return (
    <>
      <div>{children}</div>
      {perDay !== null && <div className="text-xs text-muted-foreground">{perDay}/day</div>}
    </>
  );
}

function withPerDay(value: string | null): string | null {
  return value === null ? null : `${value}/day avg`;
}

function perDayData(bytes: number, span: number): string | null {
  return span > 1 ? formatData(bytes / span) : null;
}

function perDayUsd(amount: number | null, span: number): string | null {
  return span > 1 && amount !== null ? formatUsd(amount / span) : null;
}

/** Hologram bills decimal megabytes. */
function costOf(bytes: number, perMb: number | null): number | null {
  return perMb === null ? null : (bytes / 1_000_000) * perMb;
}

function rate(perMb: number | null): string {
  return perMb !== null ? `$${perMb.toFixed(2)}` : '—';
}

function monthName(month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString(undefined, { month: 'short', timeZone: 'UTC' });
}

const getSimValue = (row: SimRow, key: string): unknown => {
  switch (key) {
    case 'name':
      return row.sim.name;
    case 'customer':
      return row.sim.customer.label;
    case 'activity':
      // State first, then the longest-running within it.
      return ['on-cellular', 'using-data', 'standby', 'inactive'].indexOf(row.sim.activity) * 1000 - Math.min(row.streak.days, 999);
    case 'total':
      return row.total;
    case 'charged':
      return row.charged ?? row.estimate ?? -1;
    case 'rate':
      return row.sim.plan.perMb ?? -1;
    default:
      return undefined;
  }
};

const getCustomerValue = (row: CustomerRow, key: string): unknown => {
  switch (key) {
    case 'label':
      return row.label;
    case 'sites':
      return row.sites.length;
    case 'active':
      return row.onCellular * 1000 + row.usingData;
    case 'total':
      return row.total;
    case 'charged':
      return row.charged ?? -1;
    default:
      return undefined;
  }
};

const ACTIVITY_EXPLAINER = (
  <div className="space-y-1.5">
    <p className="font-semibold text-foreground">Is this site running on 4G?</p>
    {(['on-cellular', 'using-data', 'standby', 'inactive'] as const).map((a) => (
      <p key={a}>
        <span className="font-medium text-foreground">{ACTIVITY_LABEL[a]}</span> — {ACTIVITY_HELP[a]}
      </p>
    ))}
  </div>
);

const TONE_LEGEND = (
  <span className="flex flex-wrap gap-x-3 gap-y-1">
    <span className="flex items-center gap-1">
      <span className="h-2.5 w-2.5 rounded-sm bg-ok" /> under 20 MB/day
    </span>
    <span className="flex items-center gap-1">
      <span className="h-2.5 w-2.5 rounded-sm bg-destructive" /> over, on G1 / Global
    </span>
    <span className="flex items-center gap-1">
      <span className="h-2.5 w-2.5 rounded-sm bg-info" /> over, on G3
    </span>
  </span>
);

const TREND_EXPLAINER = (
  <div className="space-y-1.5">
    <p>One bar per day, oldest on the left. 20 MB/day is the keepalive line.</p>
    {TONE_LEGEND}
  </div>
);

const CHARGED_EXPLAINER = (
  <div className="space-y-1.5">
    <p>
      <span className="font-medium text-foreground">Charged</span> is what Hologram actually billed — data,
      SIM renewals, and SMS — read from its statements and balance history.
    </p>
    <p>
      Figures marked <EstMark /> are estimates: projections forward, or data × the plan's per-MB rate where
      no actual charge exists yet.
    </p>
  </div>
);

export function Cellular() {
  const [view, setView] = useState<View>('sims');
  const [filter, setFilter] = useState<Filter>('attention');
  const [days, setDays] = useState<WindowDays>(30);
  const [chartMode, setChartMode] = useState<ChartMode>('top');
  const [q, setQ] = useState('');
  const [panel, setPanel] = useState<Panel>(null);

  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['cellular', 'sims'],
    queryFn: () => api.listCellularSims(),
    // The server holds an hour-long snapshot; refetching sooner would only
    // re-read the same copy.
    staleTime: 10 * 60 * 1000,
  });
  // Both pages share one snapshot, so a finished refresh updates the other's cache too.
  const reload = useCallback(
    async (since: number) => {
      queryClient.setQueryData(['cellular', 'sims'], await api.listCellularSims(since || undefined));
      await queryClient.invalidateQueries({ queryKey: ['gateways'] });
    },
    [queryClient],
  );
  const refresh = useSnapshotRefresh(query.data?.refreshRunId, reload);
  const data = query.data;
  const threshold = data?.activeDailyBytes ?? 0;
  const span = Math.min(days, data?.dates.length ?? days);
  const allDates = useMemo(() => data?.dates ?? [], [data]);
  const dates = useMemo(() => allDates.slice(-span), [allDates, span]);

  const simRows = useMemo<SimRow[]>(
    () =>
      (data?.items ?? []).map((sim) => {
        const values = sim.usage.daily.slice(-span);
        const total = sum(values);
        return {
          sim,
          window: values,
          total,
          charged: sim.charged ? sum(sim.charged.slice(-span)) : null,
          estimate: costOf(total, sim.plan.perMb),
          // Measured against when the snapshot was taken, the moment RMS was read.
          streak: streakInfo(sim, data?.activeDailyBytes ?? 0, data?.dates ?? [], Date.parse(data?.asOf ?? '')),
        };
      }),
    [data, span],
  );

  const customerRows = useMemo<CustomerRow[]>(() => {
    const groups = new Map<string, CustomerRow>();
    for (const row of simRows) {
      const { customer } = row.sim;
      const group = groups.get(customer.key) ?? {
        key: customer.key,
        label: customer.label,
        fromTag: customer.fromTag,
        organization: row.sim.organization,
        sites: [],
        onCellular: 0,
        usingData: 0,
        window: new Array<number>(row.window.length).fill(0),
        total: 0,
        charged: 0,
      };
      group.sites.push(row);
      if (row.sim.activity === 'on-cellular') group.onCellular++;
      if (row.sim.activity === 'using-data') group.usingData++;
      row.window.forEach((v, i) => (group.window[i] = (group.window[i] ?? 0) + v));
      group.total += row.total;
      group.charged = group.charged === null || row.charged === null ? null : group.charged + row.charged;
      groups.set(customer.key, group);
    }
    for (const group of groups.values()) group.sites.sort((a, b) => (b.charged ?? b.total) - (a.charged ?? a.total));
    return [...groups.values()];
  }, [simRows]);

  // Customer colours are shared by the daily chart and the flow chart, so one
  // customer is one colour everywhere on the page, and keeps it across windows.
  const [slots, setSlots] = useState<ReadonlyMap<string, number>>(() => new Map());
  const colorSlots = useMemo(() => {
    const topKeys = [...customerRows]
      .filter((c) => c.total > 0)
      .sort((a, b) => b.total - a.total)
      .slice(0, NAMED)
      .map((c) => c.key);
    const next = assignSlots(slots, topKeys);
    return sameSlots(next, slots) ? slots : next;
  }, [customerRows, slots]);
  if (colorSlots !== slots) setSlots(colorSlots);
  const [flowMetric, setFlowMetric] = useState<FlowMetric>('data');

  const needle = q.trim().toLowerCase();
  const filteredSims = useMemo(
    () =>
      simRows.filter(({ sim }) => {
        if (filter === 'attention' && sim.activity !== 'on-cellular' && sim.activity !== 'using-data') return false;
        if (filter !== 'all' && filter !== 'attention' && sim.activity !== filter) return false;
        if (!needle) return true;
        return [sim.name, sim.customer.label, sim.iccid, sim.router?.name, sim.server?.ip, sim.server?.name]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(needle));
      }),
    [simRows, filter, needle],
  );
  const simSort = useSort(filteredSims, getSimValue, { key: 'charged', direction: 'desc' });

  const filteredCustomers = useMemo(
    () =>
      customerRows.filter(
        (c) =>
          !needle ||
          c.label.toLowerCase().includes(needle) ||
          c.sites.some((s) => s.sim.name.toLowerCase().includes(needle)),
      ),
    [customerRows, needle],
  );
  const customerSort = useSort(filteredCustomers, getCustomerValue, { key: 'charged', direction: 'desc' });

  const planRows = useMemo(() => {
    const plans = new Map<
      string,
      { name: string | null; perMb: number | null; sims: number; total: number; charged: number | null }
    >();
    for (const row of simRows) {
      const key = String(row.sim.plan.id);
      const plan = plans.get(key) ?? {
        name: row.sim.plan.name,
        perMb: row.sim.plan.perMb,
        sims: 0,
        total: 0,
        charged: 0,
      };
      plan.sims++;
      plan.total += row.total;
      plan.charged = plan.charged === null || row.charged === null ? null : plan.charged + row.charged;
      plans.set(key, plan);
    }
    return [...plans.entries()].sort((a, b) => (b[1].charged ?? b[1].total) - (a[1].charged ?? a[1].total));
  }, [simRows]);

  // What the expensive-plan SIMs would have cost on G3 over the window.
  const g3Savings = useMemo(
    () =>
      sum(
        simRows
          .filter((r) => r.sim.plan.perMb !== null && r.sim.plan.perMb >= EXPENSIVE_PER_MB)
          .map((r) => (r.total / 1_000_000) * ((r.sim.plan.perMb ?? 0) - G3_PER_MB)),
      ),
    [simRows],
  );

  const chargesAvailable = Boolean(data?.charges.available);
  const windowCharged = chargesAvailable ? sum(simRows.map((r) => r.charged ?? 0)) : null;
  const windowBytes = sum(simRows.map((r) => r.total));
  const summary = data?.summary;
  const charges = data?.charges;
  const lastStatement = charges?.months.filter((m) => m.source === 'statement').at(-1) ?? null;

  const panelCustomer = panel?.kind === 'customer' ? (customerRows.find((c) => c.key === panel.key) ?? null) : null;
  const panelSim = panel?.kind === 'sim' ? (simRows.find((r) => r.sim.deviceId === panel.deviceId) ?? null) : null;
  const backTo = panel?.kind === 'sim' && panel.from ? (customerRows.find((c) => c.key === panel.from) ?? null) : null;
  const openSim = (sim: CellularSim, from: string | null = null) => setPanel({ kind: 'sim', deviceId: sim.deviceId, from });
  const openCustomer = (key: string) => setPanel({ kind: 'customer', key });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-2xl font-bold tracking-tight">Cellular</h1>
        <div className="flex items-center gap-3">
          <LoadingTicker active={query.isLoading} steps={LOAD_STEPS} slowMessage={SLOW_MESSAGE} />
          {data && !query.isLoading && (
            <span
              className="text-xs text-muted-foreground"
              title={`Hologram and RMS are read at most every ${data.cacheTtlMinutes} minutes unless refreshed. Taken ${formatDateTime(data.asOf)}.`}
            >
              Data from {formatRelative(data.asOf)}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => refresh.start()}
            disabled={refresh.running || query.isLoading}
            title="Re-read Hologram and Teltonika RMS now"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', refresh.running && 'animate-spin')} />
            {refresh.running ? 'Refreshing…' : 'Refresh'}
          </Button>
          <Segmented
            value={String(days)}
            onChange={(v) => setDays(Number(v) as WindowDays)}
            options={WINDOWS.map((d) => ({ value: String(d), label: `${d}d` }))}
            ariaLabel="Usage window"
          />
        </div>
      </div>

      {summary && charges && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          <Tile
            label="On cellular now"
            value={formatNumber(summary.onCellular)}
            tone="warn"
            hint="Confirmed by RMS"
            info={ACTIVITY_EXPLAINER}
          />
          <Tile
            label="Using data"
            value={formatNumber(summary.usingData)}
            tone="caution"
            hint="Unconfirmed by RMS"
            info={ACTIVITY_EXPLAINER}
          />
          <Tile
            label={`Charged · last ${windowLabel(days)}`}
            value={windowCharged !== null ? formatUsd(windowCharged) : '—'}
            hint={
              span > 1 && windowCharged !== null
                ? `${formatUsd(windowCharged / span)}/day · ${formatData(windowBytes / span)}/day of data`
                : `${formatData(windowBytes)} of data`
            }
            info={CHARGED_EXPLAINER}
          />
          <Tile
            label="Charged this month"
            value={charges.monthToDate !== null ? formatUsd(charges.monthToDate) : '—'}
            hint={
              <>
                Projected {formatUsd(charges.projectedMonth)} <EstMark />
              </>
            }
            info={
              <p>
                Actual charges since the 1st. The projection <EstMark /> adds the last 7 days' actual daily
                spend for each day left in the month.
              </p>
            }
          />
          <Tile
            label={lastStatement ? `${monthName(lastStatement.month)} statement` : 'Last statement'}
            value={lastStatement ? formatUsd(lastStatement.charged) : '—'}
            hint="Hologram's monthly total"
            info={<p>Total charges on Hologram's most recent monthly statement — data, SIM renewals, SMS, orders.</p>}
          />
        </div>
      )}

      {query.error && <p className="text-sm text-destructive">{(query.error as Error).message}</p>}
      {refresh.error && <p className="text-sm text-destructive">{(refresh.error as Error).message}</p>}

      <RefreshProgress progress={refresh.progress} />
      {data && <Notices data={data} />}

      {data && (
        <div className="grid gap-3 lg:grid-cols-3">
          <Card className="px-4 py-3 lg:col-span-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="flex items-center gap-1.5 text-sm font-semibold">
                  Cellular data per day
                  <InfoTip label="About this chart">
                    <p>
                      {chartMode === 'top'
                        ? "Each day's bar is split into that day's 10 biggest users — the darkest segment at the bottom is the biggest. The same customer can rank differently from day to day, so hover a segment to follow that customer across every day, and click it to open them."
                        : 'Total cellular data across every SIM, per day.'}{' '}
                      Dates are UTC; Hologram's newest day lags by up to a day.
                    </p>
                  </InfoTip>
                </h2>
                <span className="text-xs text-muted-foreground">through {data.latestDate ?? '—'} UTC</span>
              </div>
              <Segmented
                value={chartMode}
                onChange={setChartMode}
                options={[
                  { value: 'top', label: 'Top 10 per day' },
                  { value: 'fleet', label: 'Fleet total' },
                ]}
                ariaLabel="Chart breakdown"
              />
            </div>
            {chartMode === 'fleet' ? (
              <div className="mt-3 h-60">
                <UsageChart
                  days={dates.map((date, i) => ({ date, bytes: sum(simRows.map((r) => r.window[i] ?? 0)) }))}
                />
              </div>
            ) : (
              <DailyTopChart
                dates={dates}
                customers={customerRows}
                days={days}
                slots={colorSlots}
                onSelect={openCustomer}
              />
            )}
          </Card>

          <div className="space-y-3">
            <Card className="px-4 py-3">
              <h2 className="flex items-center gap-1.5 text-sm font-semibold">
                Monthly spend
                <InfoTip label="About monthly spend">
                  <p>
                    Finished months are Hologram's statement totals. The current month is actual charges to date;
                    the outlined bar <EstMark /> is the projection to month end.
                  </p>
                </InfoTip>
              </h2>
              <MonthlySpend months={charges?.months ?? []} projected={charges?.projectedMonth ?? null} />
            </Card>

            <Card className="px-4 py-3">
              <h2 className="text-sm font-semibold">Charged by plan · last {windowLabel(days)}</h2>
              <table className="mt-2 w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="py-1 font-medium">Plan</th>
                    <th className="py-1 text-right font-medium">$/MB</th>
                    <th className="py-1 text-right font-medium">SIMs</th>
                    <th className="py-1 text-right font-medium">Charged</th>
                  </tr>
                </thead>
                <tbody>
                  {planRows.map(([key, p]) => (
                    <tr key={key} className="border-t border-line-soft">
                      <td className="py-1.5 pr-2">
                        <PlanBadge name={p.name} perMb={p.perMb} />
                      </td>
                      <td
                        className={cn(
                          'py-1.5 text-right tabular-nums',
                          (p.perMb ?? 0) >= EXPENSIVE_PER_MB && 'font-medium text-caution',
                        )}
                      >
                        {rate(p.perMb)}
                      </td>
                      <td className="py-1.5 text-right tabular-nums">{formatNumber(p.sims)}</td>
                      <td className="py-1.5 text-right tabular-nums">{formatUsd(p.charged)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {g3Savings >= 1 && (
                <p className="mt-2 text-xs text-muted-foreground">
                  On G3 rates, the G1 / Global SIMs would have cost about{' '}
                  <span className="font-medium text-foreground">{formatUsd(g3Savings)}</span> less over this window <EstMark />
                </p>
              )}
            </Card>
          </div>
        </div>
      )}

      {data && (
        <Card className="px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="flex items-center gap-1.5 text-sm font-semibold">
              Where it goes · last {windowLabel(days)}
              <InfoTip label="About this diagram">
                <p>
                  All cellular {flowMetric === 'data' ? 'data' : 'charges'} for the window, split by customer and then by
                  site. The {FLOW_CUSTOMERS} biggest customers and their {FLOW_SITES} biggest sites get their own
                  branches; the rest fold into "+N more". Click a customer or site to open it.
                </p>
              </InfoTip>
            </h2>
            <Segmented
              value={flowMetric}
              onChange={setFlowMetric}
              options={[
                { value: 'data', label: 'Data' },
                { value: 'charged', label: 'Charged' },
              ]}
              ariaLabel="Flow measure"
            />
          </div>
          <FlowChart
            customers={customerRows}
            metric={flowMetric}
            slots={colorSlots}
            onCustomer={openCustomer}
            onSim={(deviceId) => setPanel({ kind: 'sim', deviceId, from: null })}
          />
        </Card>
      )}

      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3">
          <Segmented
            value={view}
            onChange={setView}
            options={[
              { value: 'sims', label: 'SIMs' },
              { value: 'customers', label: 'Customers' },
              { value: 'routers', label: `Other routers${data ? ` (${data.unmatchedRouters.length})` : ''}` },
            ]}
            ariaLabel="Table"
          />
          <div className="relative w-72">
            <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search SIM, customer, ICCID, router, server…"
              className="pl-9"
            />
          </div>
          {view === 'sims' && (
            <Segmented value={filter} onChange={setFilter} options={FILTERS} ariaLabel="Activity filter" />
          )}
          <span className="ml-auto text-xs text-muted-foreground">{query.isLoading ? 'Loading…' : ''}</span>
        </div>

        <div className="overflow-x-auto">
          {view === 'sims' && (
            <table className="w-full text-sm">
              <thead className="border-b border-line bg-muted/50">
                <tr>
                  <SortableHeader label="SIM" sortKey="name" sort={simSort.sort} onToggle={simSort.toggle} />
                  <SortableHeader label="Customer" sortKey="customer" sort={simSort.sort} onToggle={simSort.toggle} />
                  <SortableHeader
                    label="Activity"
                    sortKey="activity"
                    sort={simSort.sort}
                    onToggle={simSort.toggle}
                    extra={<InfoTip label="What the activity states mean">{ACTIVITY_EXPLAINER}</InfoTip>}
                  />
                  <th className="px-4 py-2 text-left font-medium text-muted-foreground">Router</th>
                  <TrendHeader days={days} />
                  <SortableHeader label="Data" sortKey="total" sort={simSort.sort} onToggle={simSort.toggle} />
                  <SortableHeader
                    label="Charged"
                    sortKey="charged"
                    sort={simSort.sort}
                    onToggle={simSort.toggle}
                    extra={<InfoTip label="Actual versus estimated cost">{CHARGED_EXPLAINER}</InfoTip>}
                  />
                  <SortableHeader label="Plan" sortKey="rate" sort={simSort.sort} onToggle={simSort.toggle} />
                </tr>
              </thead>
              <tbody>
                {query.isLoading && (
                  <tr>
                    <td colSpan={8} className="px-4 py-6 text-center">
                      <LoadingTicker active steps={LOAD_STEPS} slowMessage={SLOW_MESSAGE} />
                    </td>
                  </tr>
                )}
                {!query.isLoading &&
                  simSort.sortedItems.map((row) => (
                    <SimTableRow
                      key={row.sim.deviceId}
                      row={row}
                      threshold={threshold}
                      days={days}
                      onOpen={(sim) => openSim(sim)}
                    />
                  ))}
                {!query.isLoading && simSort.sortedItems.length === 0 && (
                  <EmptyRow
                    cols={8}
                    text={filter === 'attention' ? 'No site is running on cellular right now.' : 'No SIMs match.'}
                  />
                )}
              </tbody>
            </table>
          )}

          {view === 'customers' && (
            <table className="w-full text-sm">
              <thead className="border-b border-line bg-muted/50">
                <tr>
                  <SortableHeader label="Customer" sortKey="label" sort={customerSort.sort} onToggle={customerSort.toggle} />
                  <SortableHeader label="Sites" sortKey="sites" sort={customerSort.sort} onToggle={customerSort.toggle} />
                  <SortableHeader
                    label="Activity"
                    sortKey="active"
                    sort={customerSort.sort}
                    onToggle={customerSort.toggle}
                    extra={<InfoTip label="What the activity states mean">{ACTIVITY_EXPLAINER}</InfoTip>}
                  />
                  <TrendHeader days={days} />
                  <SortableHeader label="Data" sortKey="total" sort={customerSort.sort} onToggle={customerSort.toggle} />
                  <SortableHeader
                    label="Charged"
                    sortKey="charged"
                    sort={customerSort.sort}
                    onToggle={customerSort.toggle}
                    extra={<InfoTip label="Actual versus estimated cost">{CHARGED_EXPLAINER}</InfoTip>}
                  />
                </tr>
              </thead>
              <tbody>
                {query.isLoading && <EmptyRow cols={6} text="Loading…" />}
                {customerSort.sortedItems.map((c) => (
                  <tr
                    key={c.key}
                    onClick={() => openCustomer(c.key)}
                    className={cn(
                      'cursor-pointer border-b border-line-soft last:border-0 hover:bg-raise',
                      c.onCellular > 0 && 'shadow-[inset_2px_0_0_var(--color-warn)]',
                      c.onCellular === 0 && c.usingData > 0 && 'shadow-[inset_2px_0_0_var(--color-caution)]',
                    )}
                  >
                    <td className="px-4 py-2 font-medium">
                      {c.label}
                      {c.fromTag && (
                        <span
                          className="ml-2 text-xs font-normal text-muted-foreground"
                          title="No X5 organization resolved; grouped by Hologram tag"
                        >
                          tag
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 tabular-nums">{formatNumber(c.sites.length)}</td>
                    <td className="px-4 py-2 text-xs">
                      <ActivityCounts onCellular={c.onCellular} usingData={c.usingData} />
                    </td>
                    <td className="px-4 py-2">
                      <Trend series={seriesOf(c.sites.map((s) => s.sim), days)} threshold={threshold * c.sites.length} />
                    </td>
                    <td className="px-4 py-2 tabular-nums">
                      <WithAverage perDay={perDayData(c.total, span)}>{formatData(c.total)}</WithAverage>
                    </td>
                    <td className="px-4 py-2 tabular-nums">
                      <WithAverage perDay={perDayUsd(c.charged, span)}>
                        <Money actual={c.charged} estimate={sum(c.sites.map((s) => s.estimate ?? 0))} />
                      </WithAverage>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {view === 'routers' && data && (
            <>
              <p className="border-b border-line-soft px-4 py-2 text-xs text-muted-foreground">
                Routers in RMS whose SIM is not a live SIM in this Hologram account — another carrier's SIM, a
                spare, or a test unit. Their cellular spend is not visible here.
              </p>
              <table className="w-full text-sm">
                <thead className="border-b border-line bg-muted/50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-muted-foreground">Router</th>
                    <th className="px-4 py-2 text-left font-medium text-muted-foreground">ICCID</th>
                    <th className="px-4 py-2 text-left font-medium text-muted-foreground">Operator</th>
                    <th className="px-4 py-2 text-left font-medium text-muted-foreground">WAN</th>
                  </tr>
                </thead>
                <tbody>
                  {data.unmatchedRouters
                    .filter((r) => !needle || r.name.toLowerCase().includes(needle) || (r.iccid ?? '').includes(needle))
                    .map((r) => (
                      <tr key={r.id} className="border-b border-line-soft last:border-0">
                        <td className="px-4 py-2 font-medium">{r.name}</td>
                        <td className="px-4 py-2 font-mono text-xs">{r.iccid ?? '—'}</td>
                        <td className="px-4 py-2">{r.operator ?? '—'}</td>
                        <td className="px-4 py-2 text-xs">
                          <WanLabel wan={r.wan} fresh={r.fresh} lastUpdateAt={r.lastUpdateAt} />
                        </td>
                      </tr>
                    ))}
                  {data.unmatchedRouters.length === 0 && (
                    <EmptyRow cols={4} text="Every router in RMS holds a known SIM." />
                  )}
                </tbody>
              </table>
            </>
          )}
        </div>
      </Card>

      <Drawer
        open={panel !== null}
        onClose={() => setPanel(null)}
        className="max-w-3xl"
        title={
          panelCustomer ? (
            <div>
              <h2 className="text-base font-semibold">{panelCustomer.label}</h2>
              <p className="text-xs text-muted-foreground">
                {formatNumber(panelCustomer.sites.length)} site{panelCustomer.sites.length === 1 ? '' : 's'} · last{' '}
                {windowLabel(days)}
              </p>
            </div>
          ) : panelSim ? (
            <div>
              {backTo && (
                <button
                  type="button"
                  onClick={() => openCustomer(backTo.key)}
                  className="mb-1 flex items-center gap-1 text-xs text-accent hover:underline"
                >
                  <ArrowLeft className="h-3 w-3" /> {backTo.label}
                </button>
              )}
              <h2 className="text-base font-semibold">{panelSim.sim.name}</h2>
              <p className="text-xs text-muted-foreground">
                {panelSim.sim.customer.label} · last {windowLabel(days)}
              </p>
            </div>
          ) : null
        }
      >
        {panelCustomer && (
          <CustomerDetail
            customer={panelCustomer}
            allDates={allDates}
            days={days}
            threshold={threshold}
            onOpenSim={(sim) => openSim(sim, panelCustomer.key)}
          />
        )}
        {panelSim && (
          <SimDetail
            row={panelSim}
            allDates={allDates}
            days={days}
            threshold={threshold}
            onOpenCustomer={() => openCustomer(panelSim.sim.customer.key)}
          />
        )}
      </Drawer>
    </div>
  );
}

function TrendHeader({ days }: { days: number }) {
  return (
    <th className="px-4 py-3 text-left font-medium text-muted-foreground">
      <span className="flex items-center gap-1">
        Trend{days < MIN_TREND_DAYS ? ` (${MIN_TREND_DAYS}d)` : ''}
        <InfoTip label="How to read the trend">{TREND_EXPLAINER}</InfoTip>
      </span>
    </th>
  );
}

/** A small, consistent marker for an estimated figure, so it never passes for an actual one. */
function EstMark() {
  return (
    <span
      className="rounded border border-dashed border-line px-1 text-[10px] font-medium tracking-wide text-muted-foreground uppercase"
      title="Estimated, not an actual charge"
    >
      est.
    </span>
  );
}

/** Actual where Hologram has charged; the estimate, marked as such, only when it has not. */
function Money({ actual, estimate }: { actual: number | null; estimate: number | null }) {
  if (actual !== null) return <>{formatUsd(actual)}</>;
  if (estimate === null) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="text-muted-foreground">
      {formatUsd(estimate)} <EstMark />
    </span>
  );
}

function MonthlySpend({
  months,
  projected,
}: {
  months: { month: string; charged: number; source: 'statement' | 'to-date' }[];
  projected: number | null;
}) {
  if (months.length === 0) return <p className="mt-2 text-xs text-muted-foreground">No statements yet.</p>;
  const max = Math.max(...months.map((m) => m.charged), projected ?? 0, 1);
  return (
    <ul className="mt-2 space-y-1.5">
      {months.map((m) => {
        const toDate = m.source === 'to-date';
        return (
          <li key={m.month} className="flex items-center gap-2 text-xs">
            <span className="w-8 text-muted-foreground">{monthName(m.month)}</span>
            <span className="relative h-3 flex-1">
              {toDate && projected !== null && (
                <span
                  className="absolute inset-y-0 left-0 rounded-sm border border-dashed border-accent"
                  style={{ width: `${(projected / max) * 100}%` }}
                />
              )}
              <span
                className={cn('absolute inset-y-0 left-0 rounded-sm', toDate ? 'bg-accent' : 'bg-live')}
                style={{ width: `${(m.charged / max) * 100}%` }}
              />
            </span>
            <span className="w-28 text-right tabular-nums">
              {formatUsd(m.charged)}
              {toDate && <span className="text-muted-foreground"> so far</span>}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function SimTableRow({
  row,
  threshold,
  days,
  onOpen,
  compact,
}: {
  row: SimRow;
  threshold: number;
  days: number;
  onOpen: (sim: CellularSim) => void;
  /** The narrower layout inside a customer drawer: no customer column, WAN folded under activity. */
  compact?: boolean;
}) {
  const { sim } = row;
  const cell = compact ? 'px-3 py-2' : 'px-4 py-2';
  return (
    <tr
      onClick={() => onOpen(sim)}
      className={cn(
        'cursor-pointer border-b border-line-soft last:border-0 hover:bg-raise',
        sim.activity === 'on-cellular' && 'shadow-[inset_2px_0_0_var(--color-warn)]',
        sim.activity === 'using-data' && 'shadow-[inset_2px_0_0_var(--color-caution)]',
      )}
    >
      <td className={cn(cell, 'max-w-64')}>
        <div className="truncate font-medium">{sim.name}</div>
        {!compact && <div className="font-mono text-xs text-muted-foreground">{sim.iccid ?? '—'}</div>}
      </td>
      {!compact && (
        <td className="max-w-56 px-4 py-2">
          <CustomerCell sim={sim} />
        </td>
      )}
      <td className={cn(cell, 'whitespace-nowrap')}>
        <ActivityBadge activity={sim.activity} title={sim.activityReason} />
        <Streak sim={sim} info={row.streak} />
        {compact && sim.router && (
          <div className="mt-0.5 text-xs">
            <WanLabel wan={sim.router.wan} fresh={sim.router.fresh} lastUpdateAt={sim.router.lastUpdateAt} />
          </div>
        )}
      </td>
      {!compact && (
        <td className="px-4 py-2 text-xs">
          <RouterCell router={sim.router} />
        </td>
      )}
      <td className={cell}>
        <Trend series={seriesOf([sim], days)} threshold={threshold} width={compact ? 90 : 120} />
      </td>
      <td className={cn(cell, 'whitespace-nowrap tabular-nums')}>
        <WithAverage perDay={perDayData(row.total, row.window.length)}>{formatData(row.total)}</WithAverage>
      </td>
      <td className={cn(cell, 'whitespace-nowrap tabular-nums')}>
        <WithAverage perDay={perDayUsd(row.charged, row.window.length)}>
          <Money actual={row.charged} estimate={row.estimate} />
        </WithAverage>
      </td>
      <td className={cell}>
        <PlanBadge name={sim.plan.name} perMb={sim.plan.perMb} />
      </td>
    </tr>
  );
}

function CustomerDetail({
  customer,
  allDates,
  days,
  threshold,
  onOpenSim,
}: {
  customer: CustomerRow;
  allDates: string[];
  days: number;
  threshold: number;
  onOpenSim: (sim: CellularSim) => void;
}) {
  const span = Math.max(days, MIN_TREND_DAYS);
  const chartDates = allDates.slice(-span);
  const chart = seriesOf(
    customer.sites.map((s) => s.sim),
    days,
  );
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-2">
        <Stat
          label={`Charged · last ${windowLabel(days)}`}
          value={<Money actual={customer.charged} estimate={null} />}
          sub={withPerDay(perDayUsd(customer.charged, customer.window.length))}
        />
        <Stat
          label="Data"
          value={formatData(customer.total)}
          sub={withPerDay(perDayData(customer.total, customer.window.length))}
        />
        <Stat
          label="On 4G"
          value={
            customer.onCellular + customer.usingData > 0
              ? `${customer.onCellular + customer.usingData} of ${customer.sites.length}`
              : 'None'
          }
        />
      </div>

      {customer.organization ? (
        <RouterLink
          to={`/organizations/${encodeURIComponent(customer.organization.organizationId)}`}
          className="text-sm text-accent hover:underline"
        >
          Open organization →
        </RouterLink>
      ) : (
        <p className="text-xs text-muted-foreground">
          Grouped by Hologram tag — no X5 organization resolved from these sites' routers.
        </p>
      )}

      <div>
        <h3 className="text-sm font-semibold">
          Data per day, all sites{days < MIN_TREND_DAYS ? ` (last ${MIN_TREND_DAYS} days)` : ''}
        </h3>
        <div className="pb-1.5 text-xs text-muted-foreground">
          Dashed line: keepalive for {customer.sites.length} site{customer.sites.length === 1 ? '' : 's'}. {TONE_LEGEND}
        </div>
        <div className="h-40">
          <UsageChart
            days={chartDates.map((date, i) => ({
              date,
              bytes: chart.values[i] ?? 0,
              expensiveBytes: chart.expensive[i] ?? 0,
            }))}
            threshold={threshold * customer.sites.length}
          />
        </div>
      </div>

      <div>
        <h3 className="pb-1.5 text-sm font-semibold">Sites</h3>
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="w-full text-sm">
            <thead className="border-b border-line bg-muted/50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">Site</th>
                <th className="px-3 py-2 font-medium">Activity · WAN</th>
                <th className="px-3 py-2 font-medium">Trend</th>
                <th className="px-3 py-2 font-medium">Data</th>
                <th className="px-3 py-2 font-medium">Charged</th>
                <th className="px-3 py-2 font-medium">Plan</th>
              </tr>
            </thead>
            <tbody>
              {customer.sites.map((row) => (
                <SimTableRow
                  key={row.sim.deviceId}
                  row={row}
                  threshold={threshold}
                  days={days}
                  onOpen={onOpenSim}
                  compact
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Notices({ data }: { data: CellularSims }) {
  const notes: string[] = [];
  if (data.refreshFailed) {
    notes.push(
      `Hologram could not be read just now, so this is the last good snapshot, from ${formatRelative(data.asOf)}. Try Refresh again shortly.`,
    );
  }
  const stale = staleNotice(data.staleParts ?? [], formatRelative);
  if (stale) notes.push(stale);
  if (!data.charges.available) {
    notes.push("Hologram's charges have not loaded yet, so costs below are estimates. Refresh to try again.");
  } else if (data.chargesStale) {
    notes.push("Hologram's charges could not be updated on the last refresh; actual figures may be a little behind.");
  }
  if (!data.routersAvailable) {
    notes.push('Teltonika RMS could not be read on the last refresh, so router state is from the previous snapshot or unknown.');
  }
  if (data.truncated) notes.push('A Hologram or RMS listing hit its page cap; some SIMs may be missing.');
  if (data.directoryPartial) notes.push('Server or organization lookups were partial; some customers may not resolve.');
  if (notes.length === 0) return null;
  return (
    <div className="space-y-1">
      {notes.map((n) => (
        <p key={n} className="flex items-start gap-2 text-sm text-caution">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          {n}
        </p>
      ))}
    </div>
  );
}

/** How many of each day's biggest users get their own segment. */
const TOP_PER_DAY = 10;
/** Customers with a colour of their own; see the `series-*` tokens in index.css. */
const NAMED = 7;

interface DaySegment {
  key: string;
  label: string;
  bytes: number;
  /** A named customer's colour slot, or null for a grey "rest of the top 10" block. */
  slot: number | null;
}

interface DayRow {
  date: string;
  /** The day's top users by size, for the tooltip. */
  top: DaySegment[];
  /** Grey blocks, biggest first: the day's top-10 users who have no colour of their own. */
  unnamed: DaySegment[];
  other: number;
  otherCount: number;
  total: number;
  [segment: `n${number}` | `u${number}`]: number;
}

function seriesColor(slot: number): string {
  return `var(--color-series-${slot + 1})`;
}

/** Survivors keep their slot; newcomers take the lowest free ones. */
function assignSlots(previous: ReadonlyMap<string, number>, keys: string[]): Map<string, number> {
  const next = new Map<string, number>();
  for (const key of keys) {
    const slot = previous.get(key);
    if (slot !== undefined) next.set(key, slot);
  }
  const taken = new Set(next.values());
  const free = Array.from({ length: NAMED }, (_, i) => i).filter((i) => !taken.has(i));
  for (const key of keys) if (!next.has(key)) next.set(key, free.shift() ?? 0);
  return next;
}

function sameSlots(a: ReadonlyMap<string, number>, b: ReadonlyMap<string, number>): boolean {
  return a.size === b.size && [...a].every(([k, v]) => b.get(k) === v);
}

/**
 * Each day split into its top users. The window's seven biggest customers each
 * keep one colour, stacked in a fixed order at the bottom of every bar — fixed
 * order is what keeps any two colours that touch distinguishable, including for
 * colour-blind readers. The rest of that day's top 10 are grey blocks above
 * them, and everyone else a light band on top. A customer keeps its colour when
 * the window changes, so a colour never silently starts meaning someone else.
 */
function DailyTopChart({
  dates,
  customers,
  days,
  slots: keep,
  onSelect,
}: {
  dates: string[];
  customers: CustomerRow[];
  days: number;
  slots: ReadonlyMap<string, number>;
  onSelect: (key: string) => void;
}) {
  const [hovered, setHovered] = useState<string | null>(null);

  const ranked = useMemo(
    () => [...customers].filter((c) => c.total > 0).sort((a, b) => b.total - a.total),
    [customers],
  );

  const rows = useMemo<DayRow[]>(
    () =>
      dates.map((date, i) => {
        const used = customers
          .map((c) => ({ key: c.key, label: c.label, bytes: c.window[i] ?? 0, slot: keep.get(c.key) ?? null }))
          .filter((c) => c.bytes > 0)
          .sort((a, b) => b.bytes - a.bytes);
        const top = used.slice(0, TOP_PER_DAY);
        const named = used.filter((c) => c.slot !== null);
        const unnamed = top.filter((c) => c.slot === null);
        const counted = new Set([...named, ...unnamed].map((c) => c.key));
        const rest = used.filter((c) => !counted.has(c.key));
        const row: DayRow = {
          date,
          top,
          unnamed,
          other: sum(rest.map((c) => c.bytes)),
          otherCount: rest.length,
          total: sum(used.map((c) => c.bytes)),
        };
        for (let s = 0; s < NAMED; s++) row[`n${s}`] = named.find((c) => c.slot === s)?.bytes ?? 0;
        for (let u = 0; u < TOP_PER_DAY; u++) row[`u${u}`] = unnamed[u]?.bytes ?? 0;
        return row;
      }),
    [dates, customers, keep],
  );

  const legend = ranked
    .slice(0, NAMED)
    .map((c) => ({ ...c, slot: keep.get(c.key) ?? 0 }))
    .sort((a, b) => a.slot - b.slot);
  const leaders = ranked.slice(0, TOP_PER_DAY);
  const dim = (key: string | undefined) => (hovered === null || key === hovered ? 1 : 0.2);
  const keyForSlot = (s: number) => legend.find((c) => c.slot === s)?.key;

  return (
    <div>
      <div className="mt-3 h-60" onMouseLeave={() => setHovered(null)}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 4, right: 4, left: 0, bottom: 0 }} barCategoryGap="18%">
            <CartesianGrid stroke="var(--color-line-soft)" vertical={false} />
            <XAxis
              dataKey="date"
              tick={axisTick}
              tickLine={false}
              axisLine={{ stroke: 'var(--color-line)' }}
              tickFormatter={(d: string) => d.slice(5)}
              minTickGap={24}
            />
            <YAxis tick={axisTick} tickLine={false} axisLine={false} tickFormatter={axisData} width={56} />
            <Tooltip
              cursor={{ fill: 'var(--color-raise)' }}
              content={(p) => (
                <DayTooltip active={p.active} row={p.payload?.[0]?.payload as DayRow | undefined} hovered={hovered} />
              )}
            />
            {Array.from({ length: NAMED }, (_, s) => (
              <Bar
                key={`n${s}`}
                dataKey={`n${s}`}
                stackId="day"
                fill={seriesColor(s)}
                stroke="var(--color-panel)"
                strokeWidth={1}
                maxBarSize={36}
                isAnimationActive={false}
                cursor="pointer"
                fillOpacity={dim(keyForSlot(s))}
                onMouseEnter={() => setHovered(keyForSlot(s) ?? null)}
                onClick={() => {
                  const key = keyForSlot(s);
                  if (key) onSelect(key);
                }}
              />
            ))}
            {Array.from({ length: TOP_PER_DAY }, (_, u) => (
              <Bar
                key={`u${u}`}
                dataKey={`u${u}`}
                stackId="day"
                fill="var(--color-idle)"
                stroke="var(--color-panel)"
                strokeWidth={1}
                maxBarSize={36}
                isAnimationActive={false}
                cursor="pointer"
                onMouseEnter={(entry) => setHovered((entry.payload as DayRow | undefined)?.unnamed[u]?.key ?? null)}
                onClick={(entry) => {
                  const key = (entry.payload as DayRow | undefined)?.unnamed[u]?.key;
                  if (key) onSelect(key);
                }}
              >
                {rows.map((row) => (
                  <Cell key={row.date} fillOpacity={dim(row.unnamed[u]?.key)} />
                ))}
              </Bar>
            ))}
            <Bar
              dataKey="other"
              stackId="day"
              fill="var(--color-line)"
              stroke="var(--color-panel)"
              strokeWidth={1}
              maxBarSize={36}
              isAnimationActive={false}
              fillOpacity={hovered === null ? 1 : 0.2}
              onMouseEnter={() => setHovered(null)}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        {legend.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => onSelect(c.key)}
            onMouseEnter={() => setHovered(c.key)}
            onMouseLeave={() => setHovered(null)}
            className="flex items-center gap-1 text-fg-2 hover:text-foreground hover:underline"
          >
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: seriesColor(c.slot) }} />
            {c.label}
          </button>
        ))}
        <span className="flex items-center gap-1 text-muted-foreground">
          <span className="h-2.5 w-2.5 rounded-sm bg-idle" />
          rest of that day's top 10
        </span>
        <span className="flex items-center gap-1 text-muted-foreground">
          <span className="h-2.5 w-2.5 rounded-sm bg-line" />
          everyone else
        </span>
      </div>

      {leaders.length > 0 && (
        <div className="mt-3 border-t border-line-soft pt-2">
          <h3 className="pb-1 text-xs font-semibold text-muted-foreground">
            Top users over the last {windowLabel(days)}
          </h3>
          <ol className="grid gap-x-6 sm:grid-cols-2">
            {leaders.map((c, i) => {
              const slot = keep.get(c.key);
              return (
                <li key={c.key}>
                  <button
                    type="button"
                    onClick={() => onSelect(c.key)}
                    onMouseEnter={() => setHovered(c.key)}
                    onMouseLeave={() => setHovered(null)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded px-1 py-0.5 text-left text-xs hover:bg-raise',
                      hovered === c.key && 'bg-raise',
                    )}
                  >
                    <span className="w-4 text-right text-muted-foreground tabular-nums">{i + 1}</span>
                    <span
                      className={cn('h-2 w-2 shrink-0 rounded-sm', slot === undefined && 'bg-idle')}
                      style={slot !== undefined ? { background: seriesColor(slot) } : undefined}
                    />
                    <span className="min-w-0 flex-1 truncate text-fg-2">{c.label}</span>
                    <span className="text-muted-foreground tabular-nums">{formatData(c.total)}</span>
                    <span className="w-20 text-right tabular-nums">
                      <Money actual={c.charged} estimate={null} />
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
}

function DayTooltip({ active, row, hovered }: { active?: boolean; row?: DayRow; hovered: string | null }) {
  if (!active || !row) return null;
  return (
    <div className="min-w-52 rounded-md border border-line bg-panel px-3 py-2 text-xs shadow-md">
      <div className="flex items-baseline justify-between gap-4">
        <span className="font-medium">{row.date}</span>
        <span className="font-semibold tabular-nums">{formatData(row.total)}</span>
      </div>
      {row.top.length === 0 ? (
        <div className="mt-1 text-muted-foreground">No cellular data this day</div>
      ) : (
        <ol className="mt-1.5 space-y-0.5">
          {row.top.map((c) => (
            <li
              key={c.key}
              className={cn('flex items-center justify-between gap-4', c.key === hovered && 'font-semibold')}
            >
              <span className="flex min-w-0 items-center gap-1.5 text-fg-2">
                <span
                  className={cn('h-2 w-2 shrink-0 rounded-sm', c.slot === null && 'bg-idle')}
                  style={c.slot !== null ? { background: seriesColor(c.slot) } : undefined}
                />
                <span className="truncate">{c.label}</span>
              </span>
              <span className="text-muted-foreground tabular-nums">{formatData(c.bytes)}</span>
            </li>
          ))}
          {row.total > sum(row.top.map((c) => c.bytes)) && (
            <li className="flex items-center justify-between gap-4 text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 shrink-0 rounded-sm bg-line" />
                everyone else
              </span>
              <span className="tabular-nums">{formatData(row.total - sum(row.top.map((c) => c.bytes)))}</span>
            </li>
          )}
        </ol>
      )}
    </div>
  );
}

/** Past this many days a streak is just "a long time"; the page only reasons about the last month. */
const STREAK_CAP = 30;

/**
 * How many days, counting back from the newest, the SIM has been in its current
 * state — judged on the same 20 MB/day line as the charts. On 4G counts days
 * over the line, standby days under it, inactive days with no data at all. A
 * SIM RMS just saw switch to 4G can be at 0: its last full day was still idle.
 */
function streakOf(sim: CellularSim, threshold: number): number {
  const onFourG = sim.activity === 'on-cellular' || sim.activity === 'using-data';
  const holds = (bytes: number) =>
    onFourG ? bytes >= threshold : sim.activity === 'inactive' ? bytes === 0 : bytes < threshold;
  let days = 0;
  for (let i = sim.usage.daily.length - 1; i >= 0 && days <= STREAK_CAP; i--) {
    if (!holds(sim.usage.daily[i] ?? 0)) break;
    days++;
  }
  return days;
}

interface StreakInfo {
  days: number;
  /** When the current state began: exact from RMS for a router on 4G, else the first day of the run. */
  since: string | null;
  exact: boolean;
  /** Longer than the history we read, so `since` is unknown or a lower bound. */
  beyond: boolean;
}

/**
 * A router RMS reports on 4G has an exact start from RMS's own history; for
 * everything else the start is the first day of the Hologram streak.
 */
function streakInfo(sim: CellularSim, threshold: number, allDates: string[], now: number): StreakInfo {
  const fromRms = sim.activity === 'on-cellular' ? sim.router?.mobileSince : null;
  if (fromRms) {
    const since = utcIso(fromRms) ?? fromRms;
    const days = Math.max(0, Math.floor((now - Date.parse(since)) / 86_400_000));
    const beyond = Boolean(sim.router?.mobileSinceBeyond);
    return { days: beyond ? Math.max(days, STREAK_CAP + 1) : days, since, exact: true, beyond };
  }
  const days = streakOf(sim, threshold);
  if (days > STREAK_CAP) return { days, since: null, exact: false, beyond: true };
  const first = days > 0 ? allDates[allDates.length - days] : undefined;
  return { days, since: first ? `${first}T00:00:00Z` : null, exact: false, beyond: false };
}

function formatSince(iso: string, exact: boolean): string {
  return new Date(iso).toLocaleString(
    undefined,
    exact
      ? { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }
      : { month: 'short', day: 'numeric', timeZone: 'UTC' },
  );
}

function Streak({ sim, info, long }: { sim: CellularSim; info: StreakInfo; long?: boolean }) {
  const onFourG = sim.activity === 'on-cellular' || sim.activity === 'using-data';
  const state = sim.activity === 'inactive' ? 'No data' : onFourG ? 'On 4G' : 'Idle';
  const sinceText = info.since ? formatSince(info.since, info.exact) : null;
  const title = info.beyond
    ? `${state} for over ${STREAK_CAP} days${sinceText ? ` (since before ${sinceText})` : ''}`
    : sinceText
      ? `${state} since ${sinceText}${info.exact ? ' (from RMS)' : ''}`
      : 'Just changed — its last full day was not yet over 20 MB';
  let text: string;
  if (info.beyond) text = long ? `for ${STREAK_CAP}+ days` : `${STREAK_CAP}d+`;
  else if (!sinceText) text = 'new';
  else if (long) text = `since ${sinceText}`;
  else text = info.days === 0 ? '<1d' : `${info.days}d`;
  return (
    <span className="ml-1.5 text-xs whitespace-nowrap text-muted-foreground tabular-nums" title={title}>
      {text}
    </span>
  );
}

function WanCheckPanel({ sim }: { sim: CellularSim }) {
  const [started, setStarted] = useState<CellularWanCheck | null>(null);
  const start = useMutation({ mutationFn: () => api.checkCellularWan(sim.deviceId), onSuccess: setStarted });
  const poll = useQuery({
    queryKey: ['cellular', 'wan-check', started?.channel],
    queryFn: () => api.getCellularWanCheck(started!.channel, started!.routerId),
    enabled: started?.state === 'pending',
    // About 30 seconds of patience, then the result stays "no answer yet".
    refetchInterval: (q) =>
      (q.state.data?.state ?? 'pending') === 'pending' && q.state.dataUpdateCount < 15 ? 2000 : false,
  });
  const result = poll.data ?? started;
  const waiting = start.isPending || (result?.state === 'pending' && poll.isFetching !== undefined && (poll.data?.state ?? 'pending') === 'pending');

  return (
    <div className="mt-2 space-y-2 border-t border-line-soft pt-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          Ask the router why it is on 4G. Runs one read-only check through RMS; every check is recorded in the audit log.
        </p>
        <Button variant="outline" size="sm" onClick={() => start.mutate()} disabled={waiting}>
          {waiting ? 'Checking…' : result ? 'Check again' : 'Check WAN'}
        </Button>
      </div>
      {start.error && <p className="text-xs text-destructive">{(start.error as Error).message}</p>}
      {result?.state === 'pending' && (
        <p className="text-xs text-muted-foreground">
          {waiting ? 'Waiting for the router to answer…' : 'The router has not answered. It may be offline in RMS — try again later.'}
        </p>
      )}
      {result?.state === 'failed' && (
        <p className="text-xs text-caution">
          The router did not run the check — it is probably offline in RMS. {result.output}
        </p>
      )}
      {result?.state === 'done' && result.check && (
        <div
          className={cn(
            'rounded-md border px-3 py-2 text-sm',
            result.verdict === 'healthy' && 'border-info/40 bg-info/5',
            result.verdict === 'unknown' && 'border-line bg-raise',
            result.verdict !== 'healthy' && result.verdict !== 'unknown' && 'border-caution/40 bg-caution/5',
          )}
        >
          <p className="font-medium">{result.summary}</p>
          <dl className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
            <WanFact label="WAN in config" value={result.check.disabled === null ? null : result.check.disabled ? 'Disabled' : 'Enabled'} />
            <WanFact label="Port link" value={result.check.link === null ? null : result.check.link ? 'Up' : 'Down'} />
            <WanFact label="Interface" value={result.check.up === null ? null : result.check.up ? 'Up' : 'Down'} />
            <WanFact label="IP address" value={result.check.ipv4} />
            <WanFact label="Failover says" value={result.check.failover} />
            <WanFact label="Port" value={result.check.device} />
          </dl>
          <details className="mt-1.5 text-xs text-muted-foreground">
            <summary className="cursor-pointer">Raw output</summary>
            <pre className="mt-1 font-mono text-[11px] whitespace-pre-wrap">{result.output}</pre>
          </details>
        </div>
      )}
    </div>
  );
}

function WanFact({ label, value }: { label: string; value: string | null }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right">{value ?? '—'}</dd>
    </>
  );
}

type FlowMetric = 'data' | 'charged';

/** Customers that get their own branch; the rest fold into one node. */
const FLOW_CUSTOMERS = 10;
/** Sites shown per customer before the remainder folds into "+N more". */
const FLOW_SITES = 4;
/** Below these shares a branch is a hairline, so it folds into "other" instead. */
const FLOW_MIN_CUSTOMER_SHARE = 0.015;
const FLOW_MIN_SITE_SHARE = 0.05;
/** A node shorter than this gets no label (it still has a hover title), so labels never overprint. */
const FLOW_LABEL_MIN_HEIGHT = 9;

interface FlowNode {
  name: string;
  kind: 'root' | 'customer' | 'site' | 'more' | 'others';
  color: string;
  amount: number;
  customerKey?: string;
  deviceId?: number;
}

/**
 * All SIMs → customer → site, sized by the window's data or charges. Only the
 * biggest customers and their biggest sites get their own nodes: a hundred
 * hairline links is noise, and the tables have the long tail.
 */
function FlowChart({
  customers,
  metric,
  slots,
  onCustomer,
  onSim,
}: {
  customers: CustomerRow[];
  metric: FlowMetric;
  slots: ReadonlyMap<string, number>;
  onCustomer: (key: string) => void;
  onSim: (deviceId: number) => void;
}) {
  const { nodes, links, siteCount } = useMemo(() => {
    const siteValue = (r: SimRow) => (metric === 'data' ? r.total : (r.charged ?? 0));
    const customerValue = (c: CustomerRow) => (metric === 'data' ? c.total : (c.charged ?? 0));
    const ranked = customers.filter((c) => customerValue(c) > 0).sort((a, b) => customerValue(b) - customerValue(a));
    const grand = sum(ranked.map(customerValue));
    const top = ranked.slice(0, FLOW_CUSTOMERS).filter((c) => customerValue(c) >= grand * FLOW_MIN_CUSTOMER_SHARE);
    const shown = new Set(top.map((c) => c.key));
    const rest = ranked.filter((c) => !shown.has(c.key));

    const nodes: FlowNode[] = [
      { name: 'All SIMs', kind: 'root', color: 'var(--color-fg-2)', amount: sum(ranked.map(customerValue)) },
    ];
    const links: { source: number; target: number; value: number }[] = [];
    let siteCount = 0;
    for (const c of top) {
      const slot = slots.get(c.key);
      const color = slot !== undefined ? seriesColor(slot) : 'var(--color-idle)';
      const ci = nodes.push({ name: c.label, kind: 'customer', color, amount: customerValue(c), customerKey: c.key }) - 1;
      links.push({ source: 0, target: ci, value: customerValue(c) });
      const sites = c.sites.filter((s) => siteValue(s) > 0).sort((a, b) => siteValue(b) - siteValue(a));
      const own = sites
        .slice(0, FLOW_SITES)
        .filter((s) => siteValue(s) >= customerValue(c) * FLOW_MIN_SITE_SHARE);
      for (const s of own) {
        const si =
          nodes.push({ name: s.sim.name, kind: 'site', color, amount: siteValue(s), deviceId: s.sim.deviceId }) - 1;
        links.push({ source: ci, target: si, value: siteValue(s) });
        siteCount++;
      }
      const more = sites.slice(own.length);
      if (more.length) {
        const amount = sum(more.map(siteValue));
        const mi =
          nodes.push({ name: `+${more.length} more site${more.length === 1 ? '' : 's'}`, kind: 'more', color, amount, customerKey: c.key }) - 1;
        links.push({ source: ci, target: mi, value: amount });
        siteCount++;
      }
    }
    if (rest.length) {
      const amount = sum(rest.map(customerValue));
      const oi = nodes.push({ name: `${rest.length} other customers`, kind: 'others', color: 'var(--color-idle)', amount }) - 1;
      links.push({ source: 0, target: oi, value: amount });
    }
    return { nodes, links, siteCount };
  }, [customers, metric, slots]);

  if (links.length === 0) return <p className="py-6 text-center text-sm text-muted-foreground">Nothing in this window.</p>;

  const format = (v: number) => (metric === 'data' ? formatData(v) : formatUsd(v));
  const height = Math.min(820, Math.max(360, siteCount * 18));

  return (
    <div style={{ height }} className="mt-2">
      <ResponsiveContainer width="100%" height="100%">
        <Sankey
          data={{ nodes, links }}
          nodeWidth={10}
          nodePadding={6}
          iterations={64}
          align="left"
          margin={{ top: 4, right: 230, bottom: 4, left: 4 }}
          link={(p: SankeyLinkProps) => {
            const target = p.payload.target as unknown as FlowNode;
            return (
              <path
                d={`M${p.sourceX},${p.sourceY} C${p.sourceControlX},${p.sourceY} ${p.targetControlX},${p.targetY} ${p.targetX},${p.targetY}`}
                fill="none"
                stroke={target.color}
                strokeOpacity={0.3}
                strokeWidth={Math.max(1, p.linkWidth)}
              >
                <title>{`${target.name}: ${format(p.payload.value)}`}</title>
              </path>
            );
          }}
          node={(p: SankeyNodeProps) => {
            const n = p.payload as unknown as FlowNode;
            const clickable = n.kind === 'customer' || n.kind === 'site' || n.kind === 'more';
            const onClick = () => {
              if (n.kind === 'site' && n.deviceId !== undefined) onSim(n.deviceId);
              else if (n.customerKey) onCustomer(n.customerKey);
            };
            return (
              <g onClick={clickable ? onClick : undefined} style={{ cursor: clickable ? 'pointer' : 'default' }}>
                <rect x={p.x} y={p.y} width={p.width} height={Math.max(1, p.height)} fill={n.color} rx={2} />
                {(p.height >= FLOW_LABEL_MIN_HEIGHT || n.kind === 'root') && (
                <text
                  x={p.x + p.width + 6}
                  y={p.y + p.height / 2}
                  dominantBaseline="middle"
                  fontSize={11}
                  fill={n.kind === 'more' || n.kind === 'others' ? 'var(--color-muted-foreground)' : 'var(--color-foreground)'}
                  stroke="var(--color-panel)"
                  strokeWidth={3}
                  paintOrder="stroke"
                >
                  {truncateLabel(n.name, n.kind === 'site' || n.kind === 'more' ? 30 : 24)}
                  <tspan fill="var(--color-muted-foreground)"> {format(n.amount)}</tspan>
                </text>
                )}
                <title>{`${n.name}: ${format(n.amount)}`}</title>
              </g>
            );
          }}
        />
      </ResponsiveContainer>
    </div>
  );
}

function truncateLabel(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

function SimDetail({
  row,
  allDates,
  days,
  threshold,
  onOpenCustomer,
}: {
  row: SimRow;
  allDates: string[];
  days: number;
  threshold: number;
  onOpenCustomer: () => void;
}) {
  const { sim } = row;
  const { can } = useAuth();
  const canDiagnose = can('cellular.diagnose');
  const span = Math.max(days, MIN_TREND_DAYS);
  const chartDates = allDates.slice(-span);
  const chart = seriesOf([sim], days);
  const expensive = sim.plan.perMb !== null && sim.plan.perMb >= EXPENSIVE_PER_MB;
  const onG3 = expensive ? (row.total / 1_000_000) * G3_PER_MB : null;

  return (
    <div className="space-y-5">
      <div
        className={cn(
          'rounded-md border px-3 py-2.5 text-sm',
          sim.activity === 'on-cellular' && 'border-warn/40 bg-warn/5',
          sim.activity === 'using-data' && 'border-caution/40 bg-caution/5',
          (sim.activity === 'standby' || sim.activity === 'inactive') && 'border-line bg-raise',
        )}
      >
        <div className="flex items-center gap-2">
          <span className="whitespace-nowrap">
            <ActivityBadge activity={sim.activity} title={sim.activityReason} />
            <Streak sim={sim} info={row.streak} long />
          </span>
          <span className="text-fg-2">{ACTIVITY_HELP[sim.activity]}</span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Stat
          label={`Charged · last ${windowLabel(days)}`}
          value={<Money actual={row.charged} estimate={row.estimate} />}
          sub={withPerDay(perDayUsd(row.charged, row.window.length))}
        />
        <Stat
          label="Data"
          value={formatData(row.total)}
          sub={withPerDay(perDayData(row.total, row.window.length))}
        />
        <Stat label="Typical day" value={formatData(sim.usage.baselineDaily)} />
      </div>
      {onG3 !== null && row.total > 0 && (
        <p className="-mt-3 text-xs text-muted-foreground">
          This SIM is on {shortPlan(sim.plan.name)} at {rate(sim.plan.perMb)}/MB. The same data on G3 would have
          cost about <span className="font-medium text-foreground">{formatUsd(onG3)}</span> <EstMark />
        </p>
      )}

      <div>
        <h3 className="text-sm font-semibold">
          Data per day{days < MIN_TREND_DAYS ? ` (last ${MIN_TREND_DAYS} days)` : ''}
        </h3>
        <div className="pb-1.5 text-xs text-muted-foreground">{TONE_LEGEND}</div>
        <div className="h-40">
          <UsageChart
            days={chartDates.map((date, i) => ({
              date,
              bytes: chart.values[i] ?? 0,
              expensiveBytes: chart.expensive[i] ?? 0,
            }))}
            threshold={threshold}
          />
        </div>
      </div>

      <FactSection title="Customer">
        <Fact
          label="Customer"
          value={
            <button type="button" onClick={onOpenCustomer} className="text-accent hover:underline">
              {sim.customer.label} — all sites →
            </button>
          }
        />
        <Fact
          label="Organization"
          value={
            sim.organization ? (
              <RouterLink
                to={`/organizations/${encodeURIComponent(sim.organization.organizationId)}`}
                className="hover:underline"
              >
                {sim.organization.organizationName}
              </RouterLink>
            ) : sim.organizationCandidates.length > 0 ? (
              `Ambiguous: ${sim.organizationCandidates.map((c) => c.organizationName).join(', ')}`
            ) : (
              'Not resolved — grouped by Hologram tag'
            )
          }
        />
        <Fact label="X5 server" value={sim.server?.name ?? (sim.server ? 'No instance at this address' : null)} />
        <Fact label="Server IP" value={sim.server?.ip} mono />
        <Fact label="Instance state" value={sim.server?.state && sim.server.state !== 'running' ? sim.server.state : null} />
      </FactSection>

      <FactSection title="Router">
        {sim.router ? (
          <>
            <Fact label="Name" value={sim.router.name} />
            <Fact
              label="WAN"
              value={<WanLabel wan={sim.router.wan} fresh={sim.router.fresh} lastUpdateAt={sim.router.lastUpdateAt} />}
            />
            <Fact label="Last heard from" value={formatDateTime(utcIso(sim.router.lastUpdateAt))} />
            <Fact label="Operator" value={sim.router.operator} />
            <Fact label="Signal" value={sim.router.signal !== null ? `${sim.router.signal} dBm` : null} />
            <Fact label="Model" value={sim.router.model} />
            <Fact label="Serial" value={sim.router.serial} mono />
            <Fact label="IMEI" value={sim.router.imei} mono />
            <Fact
              label="Matched by"
              value={sim.router.matchedBy === 'imei' ? 'IMEI (RMS reports no matching ICCID)' : null}
            />
            {canDiagnose && (sim.activity === 'on-cellular' || sim.activity === 'using-data') && (
              <WanCheckPanel key={sim.deviceId} sim={sim} />
            )}
          </>
        ) : (
          <Fact label="Name" value="No router in RMS holds this SIM" />
        )}
      </FactSection>

      <FactSection
        title={
          <span className="flex items-center gap-1.5">
            Current billing cycle
            <InfoTip label="About billing cycles">
              Hologram bills each SIM on its own 30-day cycle, starting the day the SIM was activated — so
              this cycle does not line up with the window above. These figures are data × the plan's rate
              <EstMark />; the projection assumes the last 7 days' usage holds to the cycle's end.
            </InfoTip>
          </span>
        }
      >
        <Fact label="Used" value={formatData(sim.period.bytes)} />
        <Fact
          label="Cost"
          value={
            <>
              {formatUsd(sim.period.cost)} <EstMark />
            </>
          }
        />
        <Fact
          label="Projected"
          value={
            <>
              {formatUsd(sim.period.projectedCost)} <EstMark />
            </>
          }
        />
        <Fact label="Cycle ends" value={formatDateTime(utcIso(sim.period.endsAt))} />
        {sim.period.previous.map((p) => (
          <Fact
            key={p.index}
            label={`${p.index} cycle${p.index === 1 ? '' : 's'} ago`}
            value={
              <>
                {formatData(p.bytes)} · {formatUsd(p.cost)} <EstMark />
              </>
            }
          />
        ))}
      </FactSection>

      <FactSection title="SIM">
        <Fact label="Plan" value={<PlanBadge name={sim.plan.name} perMb={sim.plan.perMb} />} />
        <Fact label="Rate" value={sim.plan.perMb !== null ? `${rate(sim.plan.perMb)} / MB` : null} />
        <Fact label="State" value={sim.state} />
        <Fact label="ICCID" value={sim.iccid} mono />
        <Fact label="Hologram device" value={String(sim.deviceId)} mono />
      </FactSection>
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <div className="rounded-md border border-line px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-base font-semibold tabular-nums">{value}</div>
      {sub && <div className="text-xs text-muted-foreground tabular-nums">{sub}</div>}
    </div>
  );
}

function CustomerCell({ sim }: { sim: CellularSim }) {
  if (sim.organizationCandidates.length > 0) {
    return (
      <div
        className="truncate text-caution"
        title={`Several orgs on the server: ${sim.organizationCandidates.map((c) => c.organizationName).join(', ')}`}
      >
        {sim.customer.label}
      </div>
    );
  }
  return (
    <div
      className={cn('truncate', sim.customer.fromTag && 'text-muted-foreground')}
      title={sim.customer.fromTag ? 'Hologram tag; no X5 organization resolved' : (sim.server?.name ?? undefined)}
    >
      {sim.customer.label}
    </div>
  );
}

function RouterCell({ router }: { router: CellularRouter | null }) {
  if (!router) return <span className="text-muted-foreground">not in RMS</span>;
  return (
    <div className="max-w-48">
      <WanLabel wan={router.wan} fresh={router.fresh} lastUpdateAt={router.lastUpdateAt} />
      <div className="truncate text-muted-foreground" title={router.name}>
        {router.name}
      </div>
    </div>
  );
}

function WanLabel({
  wan,
  fresh,
  lastUpdateAt,
}: {
  wan: 'mobile' | 'wired' | 'unknown';
  fresh: boolean;
  lastUpdateAt: string | null;
}) {
  const label = wan === 'mobile' ? 'Mobile' : wan === 'wired' ? 'Wired' : 'Unknown';
  return (
    <span className="whitespace-nowrap">
      <span
        className={cn('font-medium', wan === 'mobile' && fresh && 'text-warn', !fresh && 'font-normal text-muted-foreground')}
      >
        {label}
      </span>
      {!fresh && (
        <span className="ml-1 text-muted-foreground">
          · {lastUpdateAt ? formatRelative(utcIso(lastUpdateAt)) : 'never'}
        </span>
      )}
    </span>
  );
}

function ActivityBadge({ activity, title }: { activity: CellularActivity; title: string }) {
  const variant =
    activity === 'on-cellular'
      ? 'destructive'
      : activity === 'using-data'
        ? 'warning'
        : activity === 'inactive'
          ? 'outline'
          : 'secondary';
  return (
    <span title={`${title}\n\n${ACTIVITY_HELP[activity]}`}>
      <Badge variant={variant} className="whitespace-nowrap">
        {ACTIVITY_LABEL[activity]}
      </Badge>
    </span>
  );
}

/** Each plan generation keeps one colour everywhere it appears; G1 stays copper because it is the expensive one. */
const PLAN_BADGE: Record<string, 'warning' | 'success' | 'info'> = {
  G1: 'warning',
  G3: 'success',
  Global: 'info',
};

function PlanBadge({ name, perMb }: { name: string | null; perMb: number | null }) {
  const label = shortPlan(name);
  const title = `${name ?? 'Unknown plan'} · ${perMb !== null ? `${rate(perMb)} / MB` : 'rate unknown'}`;
  return (
    <span title={title}>
      <Badge variant={PLAN_BADGE[label] ?? 'outline'}>{label}</Badge>
    </span>
  );
}

function ActivityCounts({ onCellular, usingData }: { onCellular: number; usingData: number }) {
  if (onCellular === 0 && usingData === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {onCellular > 0 && <Badge variant="destructive">{onCellular} on cellular</Badge>}
      {usingData > 0 && <Badge variant="warning">{usingData} using data</Badge>}
    </span>
  );
}

/**
 * A day's colour: green is the backup idling, and above the keepalive line the
 * colour says what the traffic costs — red on the expensive plans, blue on G3.
 * A day counts as expensive when most of its bytes came from G1 / Global SIMs.
 */
type DayTone = 'idle' | 'costly' | 'cheap';

const TONE_FILL: Record<DayTone, string> = {
  idle: 'var(--color-ok)',
  costly: 'var(--color-destructive)',
  cheap: 'var(--color-info)',
};

const TONE_CLASS: Record<DayTone, string> = {
  idle: 'fill-ok',
  costly: 'fill-destructive',
  cheap: 'fill-info',
};

function toneOf(bytes: number, expensiveBytes: number, threshold: number): DayTone {
  if (threshold <= 0 || bytes < threshold) return 'idle';
  return expensiveBytes * 2 >= bytes ? 'costly' : 'cheap';
}

interface DaySeries {
  values: number[];
  /** Bytes per day from SIMs on an expensive plan. */
  expensive: number[];
}

function isExpensive(sim: CellularSim): boolean {
  return sim.plan.perMb !== null && sim.plan.perMb >= EXPENSIVE_PER_MB;
}

/** Element-wise sums over the trend span, total and expensive-plan share. */
function seriesOf(sims: CellularSim[], days: number): DaySeries {
  const span = Math.max(days, MIN_TREND_DAYS);
  const values: number[] = [];
  const expensive: number[] = [];
  for (const sim of sims) {
    const costly = isExpensive(sim);
    sim.usage.daily.slice(-span).forEach((v, i) => {
      values[i] = (values[i] ?? 0) + v;
      expensive[i] = (expensive[i] ?? 0) + (costly ? v : 0);
    });
  }
  return { values, expensive };
}

/**
 * Fixed-width bars, oldest left, coloured like the drawer charts. Scaled per
 * row but never below a little over the threshold, so an idle SIM's keepalive
 * reads as low and flat rather than being stretched into a pattern.
 */
function Trend({ series, threshold, width = 120 }: { series: DaySeries; threshold: number; width?: number }) {
  const height = 20;
  const { values, expensive } = series;
  const max = Math.max(threshold * 1.25, ...values, 1);
  const step = width / Math.max(values.length, 1);
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block" aria-hidden>
      {values.map((v, i) => {
        const h = Math.max(1.5, (v / max) * height);
        return (
          <rect
            key={i}
            x={i * step}
            y={height - h}
            width={Math.max(1, step - (step > 3 ? 1 : 0))}
            height={h}
            rx={step > 3 ? 1 : 0}
            className={TONE_CLASS[toneOf(v, expensive[i] ?? 0, threshold)]}
          >
            <title>{formatData(v)}</title>
          </rect>
        );
      })}
    </svg>
  );
}

/** Rounds an axis top up to 1, 2, 2.5 or 5 × a power of ten, so the last tick is a clean number. */
function niceCeil(value: number): number {
  if (value <= 0) return 1;
  const power = 10 ** Math.floor(Math.log10(value));
  const step = [1, 2, 2.5, 5, 10].find((m) => m * power >= value) ?? 10;
  return step * power;
}

const DATA_SERIES: ChartSeries[] = [{ key: 'bytes', label: 'Data', color: 'var(--color-live)' }];

interface UsageDay extends CellularDay {
  expensiveBytes?: number;
}

/**
 * Daily bars. With a threshold, each day is toned (see `toneOf`) and the y-axis
 * always reaches a little past the threshold, so the line is on screen even for
 * a SIM that never left keepalive.
 */
function UsageChart({ days, threshold, compact }: { days: UsageDay[]; threshold?: number; compact?: boolean }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={days} margin={{ top: 4, right: 4, left: 0, bottom: 0 }} barCategoryGap="18%">
        <CartesianGrid stroke="var(--color-line-soft)" vertical={false} />
        <XAxis
          dataKey="date"
          tick={axisTick}
          tickLine={false}
          axisLine={{ stroke: 'var(--color-line)' }}
          tickFormatter={(d: string) => d.slice(5)}
          minTickGap={compact ? 40 : 24}
        />
        <YAxis
          tick={axisTick}
          tickLine={false}
          axisLine={false}
          tickFormatter={axisData}
          width={56}
          domain={threshold ? [0, (max: number) => niceCeil(Math.max(max, threshold * 1.25))] : [0, 'auto']}
        />
        <Tooltip
          cursor={{ fill: 'var(--color-raise)' }}
          content={(p) => <ChartTooltip active={p.active} payload={p.payload} label={p.label} series={DATA_SERIES} />}
        />
        {threshold ? <ReferenceLine y={threshold} stroke="var(--color-muted-foreground)" strokeDasharray="4 3" /> : null}
        <Bar
          dataKey="bytes"
          name="Data"
          fill="var(--color-live)"
          radius={compact ? 0 : [3, 3, 0, 0]}
          maxBarSize={36}
          isAnimationActive={false}
        >
          {threshold
            ? days.map((d) => (
                <Cell key={d.date} fill={TONE_FILL[toneOf(d.bytes, d.expensiveBytes ?? 0, threshold)]} />
              ))
            : null}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/**
 * One day's breakdown. Only series that moved data that day are listed,
 * largest first: Recharts' default lists every stacked series on every day,
 * which reads as "this customer used data" beside a 0.0.
 */
function ChartTooltip({
  active,
  payload,
  label,
  series,
}: {
  active?: boolean;
  payload?: ReadonlyArray<{ dataKey?: unknown; value?: unknown }>;
  label?: unknown;
  series: ChartSeries[];
}) {
  if (!active || !payload?.length) return null;
  const values = new Map(payload.map((p) => [String(p.dataKey), Number(p.value ?? 0)]));
  const rows = series
    .map((s) => ({ ...s, value: values.get(s.key) ?? 0 }))
    .filter((r) => r.value > 0)
    .sort((a, b) => b.value - a.value);
  const total = sum(rows.map((r) => r.value));
  return (
    <div className="min-w-44 rounded-md border border-line bg-panel px-3 py-2 text-xs shadow-md">
      <div className="flex items-baseline justify-between gap-4">
        <span className="font-medium">{String(label ?? '')}</span>
        <span className="font-semibold tabular-nums">{formatData(total)}</span>
      </div>
      {series.length > 1 && (
        <ul className="mt-1.5 space-y-0.5">
          {rows.map((r) => (
            <li key={r.key} className="flex items-center justify-between gap-4">
              <span className="flex min-w-0 items-center gap-1.5 text-fg-2">
                <span className="h-2 w-2 shrink-0 rounded-sm" style={{ background: r.color }} />
                <span className="truncate">{r.label}</span>
              </span>
              <span className="text-muted-foreground tabular-nums">{formatData(r.value)}</span>
            </li>
          ))}
          {rows.length === 0 && <li className="text-muted-foreground">No cellular data this day</li>}
        </ul>
      )}
    </div>
  );
}

function Segmented<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
  ariaLabel: string;
}) {
  return (
    <div role="group" aria-label={ariaLabel} className="flex items-center gap-0.5 rounded-md border border-line p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            'rounded px-2.5 py-1 text-xs font-medium transition-colors',
            value === o.value ? 'bg-accent-soft text-accent' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Tile({
  label,
  value,
  hint,
  tone,
  info,
}: {
  label: string;
  value: string;
  hint?: ReactNode;
  tone?: 'warn' | 'caution';
  info?: ReactNode;
}) {
  return (
    <Card className="px-4 py-3">
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        {label}
        {info && <InfoTip label={`About ${label}`}>{info}</InfoTip>}
      </div>
      <div
        className={cn(
          'text-2xl font-bold tabular-nums',
          tone === 'warn' && value !== '0' && 'text-warn',
          tone === 'caution' && value !== '0' && 'text-caution',
        )}
      >
        {value}
      </div>
      {hint && <div className="flex items-center gap-1 truncate text-xs text-muted-foreground">{hint}</div>}
    </Card>
  );
}

function EmptyRow({ cols, text }: { cols: number; text: string }) {
  return (
    <tr>
      <td colSpan={cols} className="px-4 py-6 text-center text-muted-foreground">
        {text}
      </td>
    </tr>
  );
}

function shortPlan(name: string | null): string {
  return (name ?? 'Unknown').replace(/ Standard Flat Rate$/, '').replace(/^Global /, '') || 'Global';
}
